import { useEffect, useMemo } from "react";

/**
 * Webfont loading for a Design Studio design.
 *
 * loadFont() used to run ONLY when the seller clicked a font in the picker, so a
 * saved design that was merely opened (or reloaded) never fetched its fonts —
 * header_font said "Bebas Neue", the CSS asked for it, and the browser quietly
 * fell back to a default serif. The live storefront had its own copy of this
 * logic, so the editor and the published store could disagree about the fonts.
 * One shared loader, used by both, keeps them honest.
 */

// Weights 300–900 so the Font Weight control has real weights to use rather than
// a synthesised bold.
export function loadGoogleFont(name) {
  if (!name || typeof document === "undefined") return;
  const href = `https://fonts.googleapis.com/css2?family=${String(name).trim().replace(/ /g, "+")}:wght@300;400;500;600;700;800;900&display=swap`;
  if (document.head.querySelector(`link[href="${href}"]`)) return;   // never stack duplicates
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.setAttribute("data-ds-font", name);
  document.head.appendChild(link);
}

// Robust fallback chain — every font here carries the ₹ (U+20B9) glyph, so if the
// chosen Google Font is missing it (some subsets are), the rupee still renders as a
// rupee instead of the browser's last-resort peso-looking substitute.
// After the system fonts come Noto's symbol / script families: sellers paste decorative text like ☆。·:*:୨୧ ʚ♡ɞ ꕤ 𓂃
// from other apps, and a font that lacks a glyph drew a blank box (▌) instead. Naming a family that was never loaded costs
// nothing, so this list is free for stores that don't use such characters — the fonts themselves are only fetched when
// a design really contains one (see loadSymbolsFor), and then only for those exact characters.
const SYMBOL_BASE = ["Noto Sans Symbols", "Noto Sans Symbols 2", "Noto Sans Math"];
const SCRIPT_FAMILIES = [
  [/[\u0B00-\u0B7F]/, "Noto Sans Oriya"], [/[\u0980-\u09FF]/, "Noto Sans Bengali"], [/[\u0900-\u097F]/, "Noto Sans Devanagari"],
  [/[\u3000-\u30FF\u3400-\u9FFF\uFF00-\uFFEF]/, "Noto Sans JP"], [/[\u1100-\u11FF\uAC00-\uD7AF]/, "Noto Sans KR"],
  [/[\uA500-\uA63F]/, "Noto Sans Vai"], [/[\u{13000}-\u{1342F}]/u, "Noto Sans Egyptian Hieroglyphs"], [/[\u1700-\u171F]/, "Noto Sans Tagalog"],
  [/[\u1BC0-\u1BFF]/, "Noto Sans Batak"], [/[\u1400-\u167F]/, "Noto Sans Canadian Aboriginal"],
];
const ALL_SYMBOL_FAMILIES = [...SYMBOL_BASE, ...SCRIPT_FAMILIES.map(([, f]) => f)];
const FALLBACK = `system-ui, -apple-system, "Segoe UI", Roboto, "Noto Sans", Arial, ${ALL_SYMBOL_FAMILIES.map(f => `"${f}"`).join(", ")}, sans-serif`;

// The characters in `text` that a normal text font might not have: everything past Latin Extended-B, minus the ranges
// every system font covers (punctuation, currency incl. ₹, variation selectors) and emoji (drawn by the OS emoji font).
export function specialChars(text) {
  const out = new Set();
  for (const ch of String(text || "")) {
    const cp = ch.codePointAt(0);
    if (cp <= 0x24F) continue;
    if ((cp >= 0x2000 && cp <= 0x206F) || (cp >= 0x20A0 && cp <= 0x20CF) || (cp >= 0xFE00 && cp <= 0xFE0F)) continue;
    if (cp >= 0x1F000 && cp <= 0x1FAFF) continue;
    out.add(ch);
  }
  return [...out].join("");
}
export function symbolFamiliesFor(chars) {
  if (!chars) return [];
  return [...SYMBOL_BASE, ...SCRIPT_FAMILIES.filter(([re]) => re.test(chars)).map(([, f]) => f)];
}
const _loadedSymbols = new Set();
// Fetch the symbol fonts for EXACTLY the characters given. Google's `text=` option returns a tiny font holding only those
// glyphs (a few hundred bytes each) instead of the full families + their ~380 unicode-range pieces, and nothing is
// requested at all when `text` has no special characters.
export function loadSymbolsFor(text) {
  if (typeof document === "undefined") return;
  const chars = specialChars(text);
  if (!chars) return;
  const key = [...chars].sort().join("");
  if (_loadedSymbols.has(key)) return;
  _loadedSymbols.add(key);
  const fams = symbolFamiliesFor(chars);
  const href = `https://fonts.googleapis.com/css2?${fams.map(f => `family=${f.replace(/ /g, "+")}`).join("&")}&text=${encodeURIComponent(chars)}&display=swap`;
  const link = document.createElement("link");
  link.rel = "stylesheet"; link.href = href; link.setAttribute("data-ds-symbols", "1");
  document.head.appendChild(link);
}
// All the text a design holds (store settings + every element's content) — what to scan for special characters.
export function designText(design, elements) {
  const parts = [];
  // Only the design fields that can hold text (the rest is colours/numbers/URLs, and can be large).
  try { parts.push(JSON.stringify([design?.header_config, design?.intro_animation, design?.layout_json?.intro_animation])); } catch (_) {}
  for (const el of elements || []) { parts.push(el?.content_html || "", el?.text || "", el?.title || "", el?.subtitle || "", el?.label || ""); }
  return parts.join(" ");
}
// Font-picker previews: so each name in the list is drawn in its own typeface (script fonts are hard to choose blind),
// load tiny subsets — only the letters needed to spell the names — in a few small requests, and only when the picker is
// open. Fonts that are already really loaded are skipped, and the real loadGoogleFont() stylesheet is added after
// these, so it always wins once a font is actually chosen.
export function previewChunks(names, size = 20) {
  const uniq = [...new Set((names || []).filter(Boolean))];
  const out = [];
  for (let i = 0; i < uniq.length; i += size) out.push(uniq.slice(i, i + size));
  return out;
}
export const previewHref = (chunk) => {
  const chars = [...new Set(chunk.join(""))].join("");
  return `https://fonts.googleapis.com/css2?${chunk.map(f => `family=${String(f).trim().replace(/ /g, "+")}`).join("&")}&text=${encodeURIComponent(chars)}&display=swap`;
};
const _previewed = new Set();
export function loadFontPreviews(names) {
  if (typeof document === "undefined") return;
  const todo = (names || []).filter(n => n && !_previewed.has(n) && !document.head.querySelector(`link[data-ds-font="${String(n).replace(/"/g, "")}"]`));
  todo.forEach(n => _previewed.add(n));
  previewChunks(todo).forEach(chunk => {
    const link = document.createElement("link");
    link.rel = "stylesheet"; link.href = previewHref(chunk); link.setAttribute("data-ds-font-preview", "1");
    document.head.appendChild(link);
  });
}

/** Quote the family and add a generic fallback, so a font that fails to load
 *  degrades to a sane sans — not the browser's default serif. */
export function fontStack(name, fallback = FALLBACK) {
  if (!name) return undefined;
  const n = String(name).trim();
  if (!n) return undefined;
  if (n.includes(",")) return n;                 // already a stack
  // "RupeeFix" (a U+20B9-only @font-face in designStudio.css) leads so the ₹ always
  // renders from a font that has the glyph, never the chosen font's peso-looking one.
  return `"RupeeFix", "${n.replace(/"/g, "")}", ${fallback}`;
}

/** Background style for a published design — mirrors Canvas.jsx getBgStyle so a themed
 *  product page paints the same background the store owner designed. Animated backgrounds
 *  fall back to their solid colour off-canvas (the keyframes live only in the studio). */
export function designBgStyle(design) {
  if (!design) return {};
  const val = design.background_value;
  const looksLikeColor = typeof val === "string" && /^(#|rgb|hsl)/i.test(val.trim());
  if (design.background_type === "animated") return { backgroundColor: design.background_color || "#fff" };
  if (design.background_type === "solid")    return { backgroundColor: design.background_color || "#fff" };
  if (design.background_type === "image") {
    if (val && !looksLikeColor) return { backgroundImage: `url(${val})`, backgroundSize: "cover", backgroundPosition: "center" };
    return { backgroundColor: (looksLikeColor ? val : design.background_color) || "#ffffff" };
  }
  if (design.background_type === "gradient" && val) return { background: val };
  return { backgroundColor: design.background_color || "#ffffff" };
}

/** Store-font treatment that cascades to the whole store: weight (bold), italic,
 *  underline and base text size. Kept separate so the canvas AND the theme wrapper apply
 *  the exact same rules. Size also sets --ds-font-scale (size/16) for components that
 *  want to scale a fixed px value proportionally. */
export function bodyFontStyle(design) {
  if (!design) return {};
  const s = {};
  if (dsSetting(design, "body_bold"))      s.fontWeight = 700;
  if (dsSetting(design, "body_italic"))    s.fontStyle = "italic";
  if (dsSetting(design, "body_underline")) s.textDecoration = "underline";
  const sz = Number(dsSetting(design, "body_font_size")) || 0;
  if (sz) s.fontSize = `${sz}px`;
  s["--ds-font-scale"] = sz ? (sz / 16).toFixed(3) : "1";
  return s;
}

/** A design setting that has no column of its own. The backend folds those into `layout_json`, and a design coming from the
 *  server (the published storefront, a product page, the editor after a reload) carries them ONLY there — the editor's own
 *  in-memory state carries them flat. Read both; a flat key wins even when it is null, so a just-reset value is not brought
 *  back by an older nested copy. (Store-wide bold/italic/underline/size were being read flat-only, so they never reached a
 *  published page.) */
export function dsSetting(design, key) {
  if (!design) return undefined;
  if (Object.prototype.hasOwnProperty.call(design, key)) return design[key];
  const lj = design.layout_json;
  if (!lj) return undefined;
  // Store-wide bold / italic / underline / size saved BEFORE this was fixed sat unused in layout_json and never showed on a
  // live store. Honouring them now would change stores overnight, so those four are read from layout_json only once the
  // seller has touched one of the controls again (the editor then stamps `body_style_v2`).
  if (BODY_STYLE_KEYS.includes(key) && !(design.body_style_v2 === true || lj.body_style_v2 === true)) return undefined;
  return lj[key];
}
export const BODY_STYLE_KEYS = ["body_bold", "body_italic", "body_underline", "body_font_size"];

/** A px size that follows the store's "Text size" (the --ds-font-scale CSS variable set on the store root by bodyFontStyle).
 *  Unset → the variable is 1 (or missing → fallback 1), so this is exactly `${n}px`. A per-element size set in the Type tab is
 *  spread after these defaults, so it still wins. */
export const scaledPx = (n) => `calc(${n}px * var(--ds-font-scale, 1))`;

/** A text size in px scaled UP by the store's "Text size" setting (base 16px). With the setting unset (or 16 or smaller) it returns exactly `${n}px`,
 *  so a store that never touched it renders the same as before. Used by pages that write their own px sizes (the product page). */
export function storeTextSize(design, n) {
  const sz = Number(dsSetting(design, "body_font_size")) || 0;
  // Only ever LARGER than the page's own size: a store text size under 16 (the slider allows 11) would shrink the labels,
  // buttons and prices on a product page to unreadable sizes. Making text bigger is the point of the setting here.
  return sz > 16 ? `${+(n * sz / 16).toFixed(2)}px` : `${n}px`;
}

/** The product description's own look. Both settings are optional: with neither set this returns {} and the description
 *  inherits the store font exactly as before.
 *  - desc_font: a font only for the description. Weight / italic / underline are reset so a store set to bold or italic
 *    script still gets a plain, readable description.
 *  - desc_bold / desc_italic / desc_underline: the seller's own format for the description (applied on top).
 *  - desc_font_size: px, 10–32. */
export const DESC_SIZE_MIN = 10, DESC_SIZE_MAX = 32;
export function descriptionStyle(design) {
  if (!design) return {};
  const s = {};
  const font = dsSetting(design, "desc_font");
  if (font) {
    s.fontFamily = fontStack(font);
    s.fontWeight = 400; s.fontStyle = "normal"; s.textDecoration = "none";
  }
  // Explicit format for the description (set after the reset above, so a chosen font can still be bold / italic).
  if (dsSetting(design, "desc_bold"))      s.fontWeight = 700;
  if (dsSetting(design, "desc_italic"))    s.fontStyle = "italic";
  if (dsSetting(design, "desc_underline")) s.textDecoration = "underline";
  const sz = Number(dsSetting(design, "desc_font_size")) || 0;
  if (sz) s.fontSize = `${Math.max(DESC_SIZE_MIN, Math.min(DESC_SIZE_MAX, sz))}px`;
  return s;
}

/** The full theme wrapper style for a design: background + body font + text colour +
 *  the header-font / card-radius CSS vars that the store's components read. Apply this to a
 *  page root so any Design-Studio store's product page wears that store's theme. */
export function designThemeStyle(design) {
  if (!design) return null;
  return {
    ...designBgStyle(design),
    fontFamily: fontStack(design.body_font || "Inter"),
    color: design.text_color || "#000",
    ...bodyFontStyle(design),
    "--ds-header-font": fontStack(design.header_font || design.body_font || "Inter"),
    "--ds-card-radius": `${design.card_radius ?? 14}px`,
  };
}

/** Collect every font a design actually uses — store fonts plus any per-element
 *  font from the Type tab — and make sure each one is loaded. */
export function useDesignFonts(design, elements) {
  const perElement = (elements || [])
    .map(el => { try { return JSON.parse(el.content_html || "{}")?.typo?.fontFamily || ""; } catch { return ""; } })
    .filter(Boolean)
    .join("|");

  // Decorative characters pasted into the design → fetch tiny per-character symbol fonts (nothing at all when there are none).
  const special = useMemo(() => specialChars(designText(design, elements)), [design?.header_config, design?.intro_animation, design?.layout_json?.intro_animation, elements]);
  useEffect(() => { if (special) loadSymbolsFor(special); }, [special]);

  useEffect(() => {
    const names = new Set();
    if (design?.header_font) names.add(design.header_font);
    if (design?.body_font)   names.add(design.body_font);
    if (dsSetting(design, "desc_font")) names.add(dsSetting(design, "desc_font"));   // the product description's own font
    if (design?.header_config?.font) names.add(design.header_config.font);   // the store header's own font
    perElement.split("|").filter(Boolean).forEach(f => names.add(f));
    names.forEach(loadGoogleFont);
  }, [design?.header_font, design?.body_font, dsSetting(design, "desc_font"), design?.header_config?.font, perElement]);
}
